import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppDispatch, RootState } from '../store';
import { setActiveBranch as setActiveBranchAction, clearNotice } from '../store/slices/branchSlice';
import { getMeAsync } from '../store/slices/authSlice';

/**
 * Multi-branch (Phase 3): the branch state moved into Redux (`store/slices/branchSlice.ts`) so the
 * API client and the header can read it. This file keeps the API the ~30 BM pages and the admin
 * banner already use, so none of them changed.
 *
 * `useBranchContext` needs no provider any more. `BranchProvider` still exists, now app-wide, and
 * does one job: turn a "branch no longer yours" notice into a toast, a refresh of /auth/me, and a
 * hop back to the dashboard. It is deliberately NOT a logout.
 */

interface BranchContextValue {
  activeBranchId: string;
  activeBranchName: string;
  setActiveBranch: (id: string, name: string) => void;
  isBranchSelected: boolean;
}

export const BranchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const notice = useSelector((s: RootState) => s.branch.notice);
  const activeBranchName = useSelector((s: RootState) => s.branch.activeBranchName);
  // A technician (multi-branch technicians) goes back to their own dashboard, a manager to theirs.
  const role = useSelector((s: RootState) => (s as any).auth?.user?.role as string | undefined);
  const homeRoute = role === 'employee' ? '/technician/dashboard' : '/erp/bm/dashboard';

  useEffect(() => {
    if (!notice) return;
    const where = notice.branchName || 'that branch';
    const back = activeBranchName ? ` You are now working in ${activeBranchName}.` : ' No branch is assigned to you now.';
    toast.error(
      notice.type === 'revoked'
        ? `Your access to ${where} was removed.${back}`
        : `${where} is no longer assigned to you.${back}`,
      { id: `branch-notice-${notice.branchId}`, duration: 7000 },
    );
    dispatch(clearNotice());
    if (notice.type === 'revoked') {
      // The list of branches came from the last /auth/me; refresh it so the switcher is exact.
      dispatch(getMeAsync());
      navigate(homeRoute, { replace: true });
    }
  }, [notice, activeBranchName, dispatch, navigate, homeRoute]);

  return <>{children}</>;
};

export const useBranchContext = (): BranchContextValue => {
  const dispatch = useDispatch<AppDispatch>();
  const activeBranchId = useSelector((s: RootState) => s.branch.activeBranchId);
  const activeBranchName = useSelector((s: RootState) => s.branch.activeBranchName);
  const setActiveBranch = useCallback(
    (id: string, name: string) => { dispatch(setActiveBranchAction({ id, name })); },
    [dispatch],
  );
  return { activeBranchId, activeBranchName, setActiveBranch, isBranchSelected: !!activeBranchId };
};
